<script setup lang="ts">
  import { toRef } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import MessageIcon from '@/shared/assets/icons/MessageIcon.svg';
  import PaperclipIcon from '@/shared/assets/icons/PaperclipIcon.svg';
  import PrinterIcon from '@/shared/assets/icons/PrinterIcon.svg';
  import { IconImage, ModalDocumentInfo, SimpleLoading, UiModalHeader } from '@/shared/ui';
  import { useBranchLogo } from '@/entities/Branch';
  import { useDocumentSendPdf } from '../model/useDocumentSendPdf';
  import { useDocumentSendDetail } from '../model/useDocumentSendDetail';

  const props = defineProps<{
    documentSendId: number | null;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const defaultBoxClass =
    'flex min-h-11 items-center rounded-lg border border-gray-02 bg-gray-02/20 px-4 py-2 text-sm text-gray-07';

  const {
    detail,
    loading,
    error,
    handleRetry,
    documentInfo,
    summaryFields,
    senderFields,
    getDocumentFields,
    getElectronicRecipientFields,
    getPhysicalRecipientFields,
  } = useDocumentSendDetail(toRef(props, 'documentSendId'));

  const { isGenerating, printDetail } = useDocumentSendPdf();
  const { currentLogoUrl, handleLogoError } = useBranchLogo();

  const handlePrint = () => {
    if (props.documentSendId === null) {
      return;
    }

    void printDetail(props.documentSendId);
  };
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="w-[95vw] max-w-6xl"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <div class="flex flex-col overflow-hidden rounded-2xl bg-white-01 shadow-lg max-h-[90vh]">
      <UiModalHeader
        class="border-b border-gray-02 px-6 py-4 text-sm"
        title="Controle de Envio"
        @close="emit('close')"
      />

      <div
        v-if="loading"
        data-testid="document-send-detail-loading"
        class="flex min-h-80 items-center justify-center px-6 py-12"
      >
        <SimpleLoading />
      </div>

      <div
        v-else-if="error"
        data-testid="document-send-detail-error"
        class="flex min-h-80 flex-col items-center justify-center gap-6 px-6 py-12 text-center"
      >
        <p class="text-sm text-red-600">
          {{ error }}
        </p>
        <button
          type="button"
          class="rounded-lg bg-[var(--theme-secondary)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          @click="handleRetry"
        >
          Tentar novamente
        </button>
      </div>

      <div
        v-else-if="detail"
        id="modal-content"
        data-testid="document-send-detail-modal"
        class="flex-1 min-h-0 overflow-y-auto px-6 py-6"
      >
        <ModalDocumentInfo
          :document-info="documentInfo"
          :type-label="detail.header.typeDescription ?? undefined"
          title="CONTROLE DE ENVIO DE DOCUMENTOS"
          :logo-url="currentLogoUrl"
          @logo-error="handleLogoError"
        />

        <div class="mb-6 flex justify-end">
          <button
            type="button"
            :disabled="isGenerating"
            data-testid="document-send-print-button"
            class="flex items-center gap-2 rounded-lg bg-[var(--theme-secondary)] px-3.5 py-3 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            @click="handlePrint"
          >
            <IconImage
              :src="PrinterIcon"
              alt="Imprimir"
            />
            <span>{{ isGenerating ? 'Gerando PDF...' : 'Imprimir' }}</span>
          </button>
        </div>

        <div class="space-y-8">
          <section
            class="grid gap-6 border-y border-gray-02 py-6 md:grid-cols-[minmax(0,1fr)_220px]"
          >
            <div
              v-for="field in summaryFields"
              :key="field.label"
              class="flex flex-col gap-2"
              :class="field.wrapperClass"
            >
              <span class="text-sm font-semibold text-gray-07">{{ field.label }}</span>
              <div :class="field.boxClass ?? defaultBoxClass">
                {{ field.value }}
              </div>
            </div>
          </section>

          <div class="space-y-8">
            <article
              v-for="document in detail.documents"
              :key="document.id"
              :data-testid="`document-send-detail-document-${document.id}`"
              class="space-y-6"
            >
              <section class="space-y-3">
                <h3 class="text-sm font-semibold text-gray-07">
                  Documento:
                </h3>
                <div class="grid gap-6 md:grid-cols-[160px_minmax(0,1fr)_96px]">
                  <div
                    v-for="field in getDocumentFields(document)"
                    :key="field.label"
                    class="flex flex-col gap-2"
                    :class="field.wrapperClass"
                  >
                    <span class="text-sm font-semibold text-gray-07">{{ field.label }}</span>
                    <div :class="field.boxClass ?? defaultBoxClass">
                      {{ field.value }}
                    </div>
                  </div>
                </div>
              </section>
            </article>

            <section
              v-if="detail.documents.length === 0"
              data-testid="document-send-detail-no-documents"
              class="space-y-3"
            >
              <h3 class="text-sm font-semibold text-gray-07">
                Documento:
              </h3>
              <div :class="defaultBoxClass">
                Nenhum documento do GD foi selecionado neste envio.
              </div>
            </section>

            <section
              v-if="detail.electronicRecipients.length > 0"
              data-testid="document-send-detail-electronic"
              class="space-y-3"
            >
              <h3 class="text-sm font-semibold text-gray-07">
                Cópia Eletrônica:
              </h3>
              <div class="space-y-4">
                <div
                  v-for="(recipient, recipientIndex) in detail.electronicRecipients"
                  :key="`electronic-${recipientIndex}`"
                  class="grid gap-6 md:grid-cols-[180px_minmax(0,1fr)_256px]"
                >
                  <div
                    v-for="field in getElectronicRecipientFields(recipient)"
                    :key="field.label"
                    class="flex flex-col gap-2"
                    :class="field.wrapperClass"
                  >
                    <span class="text-sm font-semibold text-gray-07">{{ field.label }}</span>
                    <div :class="field.boxClass ?? defaultBoxClass">
                      {{ field.value }}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section
              v-if="detail.physicalRecipients.length > 0"
              data-testid="document-send-detail-physical"
              class="space-y-3"
            >
              <h3 class="text-sm font-semibold text-gray-07">
                Cópia Física:
              </h3>
              <div class="space-y-4">
                <div
                  v-for="(recipient, recipientIndex) in detail.physicalRecipients"
                  :key="`physical-${recipientIndex}`"
                  class="grid gap-6 md:grid-cols-[180px_minmax(0,1fr)_96px]"
                >
                  <div
                    v-for="field in getPhysicalRecipientFields(recipient)"
                    :key="field.label"
                    class="flex flex-col gap-2"
                    :class="field.wrapperClass"
                  >
                    <span class="text-sm font-semibold text-gray-07">{{ field.label }}</span>
                    <div :class="field.boxClass ?? defaultBoxClass">
                      {{ field.value }}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section
              v-if="detail.attachments.length > 0"
              data-testid="document-send-detail-attachments"
              class="space-y-3"
            >
              <h3 class="text-sm font-semibold text-gray-07">
                Anexos:
              </h3>
              <div class="space-y-2">
                <div
                  v-for="(attachment, attachmentIndex) in detail.attachments"
                  :key="`attachment-${attachmentIndex}`"
                  class="flex min-h-11 items-center gap-3 rounded-lg border border-gray-02 bg-gray-02/20 px-4 py-2 text-sm text-gray-07"
                >
                  <IconImage
                    :src="PaperclipIcon"
                    alt="Anexo"
                    class="shrink-0"
                  />
                  <a
                    v-if="attachment.sourceType === 'URL' && attachment.externalUrl"
                    :href="attachment.externalUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="truncate text-[var(--theme-secondary-ink)] underline"
                  >
                    {{ attachment.fileName }}
                  </a>
                  <span
                    v-else
                    class="truncate"
                  >{{ attachment.fileName }}</span>
                </div>
              </div>
            </section>

            <section
              v-if="detail.message"
              data-testid="document-send-detail-message"
              class="space-y-3"
            >
              <h3 class="text-sm font-semibold text-gray-07">
                Mensagem:
              </h3>
              <div
                class="flex items-start gap-3 rounded-xl border border-gray-02 bg-gray-02/20 px-4 py-3 text-sm text-gray-07"
              >
                <IconImage
                  :src="MessageIcon"
                  alt="Mensagem"
                  class="mt-0.5 shrink-0"
                />
                <span class="whitespace-pre-wrap">{{ detail.message }}</span>
              </div>
            </section>
          </div>

          <section class="space-y-3 border-t border-gray-02 pt-6">
            <h3 class="text-sm font-semibold text-gray-07">
              Remetente:
            </h3>
            <div class="grid gap-6 md:grid-cols-3">
              <div
                v-for="field in senderFields"
                :key="field.label"
                class="flex flex-col gap-2"
              >
                <span class="text-sm font-semibold text-gray-07">{{ field.label }}</span>
                <div :class="field.boxClass ?? defaultBoxClass">
                  {{ field.value }}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>
